import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from '@/i18n'
import { Search, Upload, FileText, MoreVertical } from 'lucide-react'
import { toast } from 'sonner'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { apiService, type InterviewDocument } from '@/service/api'

interface Props {
  interviewId: string
}

const ALL_USERS = '__all__'

const formatFileSize = (bytes: number): string => {
  if (!Number.isFinite(bytes) || bytes <= 0) return '—'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  if (bytes < 1024 * 1024 * 1024) {
    const mb = bytes / (1024 * 1024)
    return `${mb < 10 ? mb.toFixed(1) : Math.round(mb)} MB`
  }
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`
}

const formatUploaded = (iso: string): string => {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  const diffMs = Date.now() - d.getTime()
  const diffMin = Math.floor(diffMs / 60_000)
  const diffHr = Math.floor(diffMs / 3_600_000)
  if (diffMin < 60) return diffMin <= 1 ? 'Just now' : `${diffMin} minutes ago`
  if (diffHr < 24) return `${diffHr} hour${diffHr === 1 ? '' : 's'} ago`
  return d.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' })
}

const DocumentsTab = ({ interviewId }: Props) => {
  const { t } = useTranslation()
  const [docs, setDocs] = useState<InterviewDocument[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [userFilter, setUserFilter] = useState<string>(ALL_USERS)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [uploading, setUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        setLoading(true)
        const data = await apiService.listInterviewDocuments(interviewId)
        if (cancelled) return
        setDocs(data)
        setError(null)
      } catch (err) {
        if (cancelled) return
        setError(err instanceof Error ? err.message : 'Failed to load documents')
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [interviewId, reloadKey])

  // Unique uploader names for the filter dropdown.
  const uploaders = useMemo(() => {
    const seen = new Map<string, string>()
    for (const d of docs) {
      const name = d.uploader.name?.trim() || d.uploader.email?.trim()
      if (name && !seen.has(name)) seen.set(name, name)
    }
    return Array.from(seen.values()).sort()
  }, [docs])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return docs.filter((d) => {
      if (userFilter !== ALL_USERS) {
        const uploader = d.uploader.name?.trim() || d.uploader.email?.trim() || ''
        if (uploader !== userFilter) return false
      }
      if (!q) return true
      return d.document_name.toLowerCase().includes(q)
    })
  }, [docs, search, userFilter])

  const allSelected = filtered.length > 0 && filtered.every((d) => selectedIds.has(d.id))
  const someSelected = filtered.some((d) => selectedIds.has(d.id)) && !allSelected

  const toggleAll = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (allSelected) filtered.forEach((d) => next.delete(d.id))
      else filtered.forEach((d) => next.add(d.id))
      return next
    })
  }

  const toggleRow = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const handleUploadClick = () => fileInputRef.current?.click()

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true)
    try {
      await apiService.uploadInterviewDocument(file, interviewId, { name: file.name })
      toast.success(t('hek_topic:documents.upload_success', { defaultValue: 'File uploaded.' }))
      setReloadKey((k) => k + 1)
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : t('hek_topic:documents.upload_failed', { defaultValue: 'Failed to upload file.' }),
      )
    } finally {
      setUploading(false)
      // Reset so re-uploading the same filename still fires onChange.
      e.target.value = ''
    }
  }

  const handleDownload = async (doc: InterviewDocument) => {
    try {
      const blob = await apiService.downloadInterviewDocument(interviewId, doc.id)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = doc.document_name
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : t('hek_topic:documents.download_failed', { defaultValue: 'Failed to download file.' }),
      )
    }
  }

  const handleDelete = async (doc: InterviewDocument) => {
    try {
      await apiService.deleteInterviewDocument(interviewId, doc.id)
      toast.success(t('hek_topic:documents.delete_success', { defaultValue: 'File deleted.' }))
      setReloadKey((k) => k + 1)
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : t('hek_topic:documents.delete_failed', { defaultValue: 'Failed to delete file.' }),
      )
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <h2 className="text-base font-semibold text-foreground">
        {t('hek_topic:documents.title', { defaultValue: 'Files and assets' })}
      </h2>

      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="relative w-[280px]">
            <Search className="size-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={String(t('hek_topic:documents.search', { defaultValue: 'Search' }))}
              className="pl-8 h-9"
            />
          </div>
          <Select value={userFilter} onValueChange={setUserFilter}>
            <SelectTrigger className="h-9 w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_USERS}>
                {t('hek_topic:documents.filter.all_users', { defaultValue: 'All Users' })}
              </SelectItem>
              {uploaders.map((u) => (
                <SelectItem key={u} value={u}>
                  {u}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileChange} />
        <Button size="sm" className="h-9 gap-1.5" onClick={handleUploadClick} disabled={uploading}>
          <Upload className="size-4" />
          {uploading
            ? t('hek_topic:documents.uploading', { defaultValue: 'Uploading…' })
            : t('hek_topic:documents.upload', { defaultValue: 'Upload' })}
        </Button>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">
          {t('hek_topic:documents.loading', { defaultValue: 'Loading documents…' })}
        </p>
      ) : error ? (
        <div className="flex flex-col gap-1">
          <p className="text-sm font-semibold text-foreground">
            {t('hek_topic:documents.error.title', { defaultValue: 'Failed to load documents' })}
          </p>
          <p className="text-xs text-muted-foreground max-w-[480px]">{error}</p>
        </div>
      ) : (
        <div className="rounded-lg border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-background">
              <tr className="border-b border-border text-left text-xs font-medium text-muted-foreground">
                <th className="w-10 px-4 py-3">
                  <Checkbox
                    checked={allSelected ? true : someSelected ? 'indeterminate' : false}
                    onCheckedChange={toggleAll}
                    aria-label={t('hek_topic:documents.select_all', { defaultValue: 'Select all' })}
                  />
                </th>
                <th className="px-3 py-3 font-medium">
                  {t('hek_topic:documents.col.file_name', { defaultValue: 'File name' })}
                </th>
                <th className="px-3 py-3 font-medium">
                  {t('hek_topic:documents.col.user', { defaultValue: 'User' })}
                </th>
                <th className="px-3 py-3 font-medium">
                  {t('hek_topic:documents.col.uploaded', { defaultValue: 'Uploaded' })}
                </th>
                <th className="px-3 py-3 font-medium">
                  {t('hek_topic:documents.col.size', { defaultValue: 'File size' })}
                </th>
                <th className="w-12 px-3 py-3 font-medium text-right pr-6">
                  {t('hek_topic:documents.col.actions', { defaultValue: 'Actions' })}
                </th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-16 text-center text-sm text-muted-foreground">
                    {docs.length === 0
                      ? t('hek_topic:documents.empty_all', {
                          defaultValue: 'No files uploaded yet. Click Upload to add the first file.',
                        })
                      : t('hek_topic:documents.empty_filtered', {
                          defaultValue: 'No files match your filters.',
                        })}
                  </td>
                </tr>
              ) : (
                filtered.map((doc) => {
                  const uploaderName = doc.uploader.name?.trim() || doc.uploader.email?.trim() || '—'
                  return (
                    <tr key={doc.id} className="border-t border-border hover:bg-muted/40">
                      <td className="px-4 py-3">
                        <Checkbox
                          checked={selectedIds.has(doc.id)}
                          onCheckedChange={() => toggleRow(doc.id)}
                          aria-label={t('hek_topic:documents.select_row', { defaultValue: 'Select row' })}
                        />
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-2.5">
                          <FileText className="size-4 shrink-0 text-muted-foreground" />
                          <span className="font-medium text-foreground truncate max-w-[440px]">
                            {doc.document_name}
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-3 text-muted-foreground">{uploaderName}</td>
                      <td className="px-3 py-3 text-muted-foreground tabular-nums">
                        {formatUploaded(doc.uploaded_date)}
                      </td>
                      <td className="px-3 py-3 text-muted-foreground tabular-nums">
                        {formatFileSize(doc.file_size)}
                      </td>
                      <td className="w-12 px-3 py-3 text-right pr-6">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              type="button"
                              className="inline-flex size-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
                              aria-label={t('hek_topic:documents.row_actions', { defaultValue: 'Row actions' })}
                            >
                              <MoreVertical className="size-4" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleDownload(doc)}>
                              {t('hek_topic:documents.actions.download', { defaultValue: 'Download' })}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="text-destructive focus:text-destructive"
                              onClick={() => handleDelete(doc)}
                            >
                              {t('hek_topic:documents.actions.delete', { defaultValue: 'Delete' })}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export default DocumentsTab
